import { Participation, ParticipationGroup } from '@/types/analytics';
import { Card, isProgressCard, LearningCard, ProductCard } from '@/types/learning/card';

export const getCardObjectId = (card: Card): string | undefined => {
  if (card.entity === 'product') return (card as ProductCard).objectId;

  if (card.entity === 'learning') return (card as LearningCard).objectId;

  return undefined;
};

const findCompletedParticipation = (group: ParticipationGroup): Participation | undefined => {
  if (group.isCompleted) return group;

  return group.previousParticipations?.find((p) => p.isCompleted);
};

/**
 * The catalog query embeds its own `participation` per card, which can disagree with
 * the participations service — e.g. a re-enrollment demotes the completed participation to
 * `previousParticipations`, leaving the card's copy marked as not completed. The participations
 * service is the source of truth for completion, so mark such cards completed. Only overrides
 * toward completed, and only on cards that already carry a participation.
 */
export const reconcileCardParticipations = (catalogCards: Card[], participations?: ParticipationGroup[]): Card[] => {
  if (!participations?.length) return catalogCards;

  const completedByObjectId = new Map<string, Participation>();
  participations.forEach((group) => {
    const completed = findCompletedParticipation(group);

    if (!completed) return;

    [group.objectId, group.catalogObjectId].forEach((key) => {
      if (key) completedByObjectId.set(key, completed);
    });
  });
  if (completedByObjectId.size === 0) return catalogCards;

  return catalogCards.map((card) => {
    if (!isProgressCard(card) || !card.participation || card.participation.isCompleted) return card;

    const objectId = getCardObjectId(card);
    const completed = objectId ? completedByObjectId.get(objectId) : undefined;

    if (!completed) return card;

    return {
      ...card,
      participation: {
        ...card.participation,
        isCompleted: true,
        completedAt: card.participation.completedAt || completed.completedAt,
        expiredAt: card.participation.expiredAt || completed.expiredAt,
      },
    };
  });
};
